import React, { useCallback, useMemo } from 'react';
import { CaptionLabelType, DisplayMode } from '../types';
import { CalendarInterval, CalendarInputCommonProps, CalendarItem } from './types';
import classNames from 'classnames';
import { subMilliseconds, subMonths, addMonths } from 'date-fns';
import { useProviderConfig } from '../../provider';
import { getDatePickerLocale } from '../DatePickerLocale';
import { DayPickerBase } from '../DayPickerBase';
import { useDatePickerAttributes } from '../hooks/useDatePickerAttributes';
import { DAY_RESOLUTION } from './resolution';
import {
  isSelectionFromAvailable,
  isSelectionToAvailable,
  getIntervalOrNull,
  findItemsOverlappingWithInterval,
  isInstantWithinItem,
  isIntervalOverlappingWithItem,
  isSubintervalStartOfInterval,
  isSubintervalEndOfInterval,
  isSubintervalMiddleOfInterval,
  resolveIntervalWithOffsets,
  isCursorItemComplete,
  isItemValid,
  applyItemIntervalWithFallback,
  getNextFocus,
  getNewCursorItem,
} from './utils';
import { Caption } from '../Caption';

export interface CalendarDayInputProps extends CalendarInputCommonProps {
  className?: string;
  defaultOffset?: [Date, Date];
  shouldConfirm?: boolean;
  showAllHoverTarget?: boolean;
  numberOfMonths?: number;
  displayMode: DisplayMode;
  viewMonth?: Date | null;
  keepOffset?: boolean;
  onViewMonthChange?: (viewMonth: Date | null) => void;
  onClickCaptionLabel?: (type: CaptionLabelType) => void;
  onItemClick?: (item: CalendarItem) => boolean | void;
}

export const CalendarDayInput: React.FC<CalendarDayInputProps> = ({
  className,
  constraint,
  resolution = DAY_RESOLUTION,
  items,
  cursor,
  target,
  defaultOffset,
  shouldConfirm = true,
  showAllHoverTarget,
  onCursorChange,
  onConfirm,
  numberOfMonths = 1,
  displayMode,
  disabled,
  viewMonth,
  keepOffset = true,
  onViewMonthChange,
  onClickCaptionLabel,
  onItemClick,
}) => {
  const { locale } = useProviderConfig();
  const datePickerLocale = getDatePickerLocale(locale?.lang);
  const { cellSize } = useDatePickerAttributes(displayMode);

  const currentTarget = target ?? cursor?.focus ?? 'from';

  const isDisabled = (date: Date): boolean => {
    if (disabled) {
      return true;
    }
    const interval = DAY_RESOLUTION.resolve(date);

    if (currentTarget === 'to') {
      if (cursor?.item?.from != null) {
        const from = cursor.item.from;
        if ((cursor.toggled || target === 'to') && interval.to <= from) {
          return true;
        }
      }
      return !isSelectionToAvailable(constraint, { items }, cursor?.item ?? {}, interval, !cursor?.toggled);
    }

    if (currentTarget === 'from' && cursor?.item?.to != null) {
      const to = cursor.item.to;
      if ((cursor.toggled || target === 'from') && interval.from >= to) {
        return true;
      }
    }

    return !isSelectionFromAvailable(constraint, { items }, cursor?.item ?? {}, interval, !cursor?.toggled);
  };

  const modifiers = useMemo(() => {
    const cursorInterval = getIntervalOrNull(cursor?.item);
    const hoverInterval =
      !!cursor?.fromHover || !!cursor?.toHover
        ? getIntervalOrNull({
            from: cursor?.fromHover ?? cursor?.item?.from,
            to: cursor?.toHover ?? cursor?.item?.to,
          })
        : null;
    const withDayInterval = (fn: (di: CalendarInterval) => boolean) => (date: Date) => {
      const dayInterval = DAY_RESOLUTION.resolve(date);
      return fn(dayInterval);
    };
    const withCheckItems = (check: (di: CalendarInterval, interval: CalendarInterval) => boolean) => (date: Date) => {
      const dayInterval = DAY_RESOLUTION.resolve(date);
      if (cursorInterval && check(dayInterval, cursorInterval)) {
        return true;
      }
      return findItemsOverlappingWithInterval(items, dayInterval).some(
        (item) => item.id !== cursor?.item?.id && check(dayInterval, item),
      );
    };
    return {
      selected: withDayInterval((di) => {
        if (findItemsOverlappingWithInterval(items, di).some((v) => v.id !== cursor?.item?.id)) {
          return true;
        }
        if (cursor?.item?.from && isInstantWithinItem(di, cursor.item.from)) return true;
        if (cursor?.item?.to && isInstantWithinItem(di, subMilliseconds(cursor.item.to, 1))) return true;
        if (cursorInterval && isIntervalOverlappingWithItem(di, cursorInterval)) {
          return true;
        }
        return false;
      }),
      range_start: withCheckItems((di, interval) => isSubintervalStartOfInterval(di, interval)),
      range_end: withCheckItems((di, interval) => isSubintervalEndOfInterval(di, interval)),
      range_middle: withCheckItems((di, interval) => isSubintervalMiddleOfInterval(di, interval)),
      hover_outside_range: withDayInterval((di) => {
        if (currentTarget === 'all') {
          // 날짜가 선택된 범위의 시작일과 종료일에 동시에 해당하지 않는 경우에만 hover 노출
          // (즉 하루 선택의 경우에는 범위가 보이지 않지만, 2일 이상 선택하는 경우에만 노출됨)
          return (
            hoverInterval != null &&
            isIntervalOverlappingWithItem(di, hoverInterval) &&
            !(isSubintervalStartOfInterval(di, hoverInterval) && isSubintervalEndOfInterval(di, hoverInterval))
          );
        }
        return hoverInterval != null && isIntervalOverlappingWithItem(di, hoverInterval);
      }),
      hover_inside_range: withCheckItems((di, interval) => {
        return (
          isIntervalOverlappingWithItem(di, interval) &&
          hoverInterval != null &&
          isIntervalOverlappingWithItem(di, hoverInterval)
        );
      }),
    } as const;
  }, [items, cursor, currentTarget]);

  const handleDayMouseEnter = (date: Date, mod: { disabled?: boolean } = {}) => {
    if (mod.disabled) return;
    const { item } = applyItemIntervalWithFallback(
      cursor?.item ?? {},
      constraint,
      { items },
      resolveIntervalWithOffsets(
        resolution,
        date,
        keepOffset ? cursor?.item?.from ?? defaultOffset?.[0] : undefined,
        keepOffset ? cursor?.item?.to ?? defaultOffset?.[1] : undefined,
      ),
      resolution.resolve(date),
      currentTarget,
    );
    if (currentTarget === 'all') {
      onCursorChange({ ...cursor, fromHover: item?.from, toHover: item?.to });
      return;
    }
    if (!cursor?.active) {
      return;
    }
    if (currentTarget === 'from' || cursor.focus == null) {
      onCursorChange({ ...cursor, fromHover: item?.from });
      return;
    }
    if (currentTarget === 'to' && cursor.item?.from != null) {
      onCursorChange({ ...cursor, toHover: item?.to });
      return;
    }
  };

  const handleDayMouseLeave = () => {
    if (cursor?.fromHover != null || cursor?.toHover != null) {
      onCursorChange({ ...cursor, fromHover: undefined, toHover: undefined });
      return;
    }
  };

  const handleDayClick = (date: Date, mod: { disabled?: boolean } = {}) => {
    if (mod.disabled) return;

    const interval = resolution.resolve(date);
    if (cursor?.item == null) {
      const selectedItems = findItemsOverlappingWithInterval(items, interval).filter((v) => v.id !== cursor?.item?.id);
      if (selectedItems.length > 0) {
        const result = onItemClick?.(selectedItems[0]);
        if (result === false) {
          return;
        }
      }
    }

    const { item } = applyItemIntervalWithFallback(
      cursor?.item ?? getNewCursorItem(),
      constraint,
      { items },
      resolveIntervalWithOffsets(
        resolution,
        date,
        keepOffset ? cursor?.item?.from ?? defaultOffset?.[0] : undefined,
        keepOffset ? cursor?.item?.to ?? defaultOffset?.[1] : undefined,
      ),
      interval,
      currentTarget,
    );

    if (shouldConfirm) {
      if (isCursorItemComplete(item) && isItemValid(constraint, { items }, item)) {
        onConfirm(item);
      } else {
        onCursorChange({ ...cursor, item, focus: getNextFocus(cursor, target), toggled: true });
      }
    } else {
      onCursorChange({ ...cursor, item, focus: cursor?.focus });
    }
  };

  const viewShiftedMonth = useMemo(() => {
    return subMonths(viewMonth ?? new Date(), Math.floor((numberOfMonths - 1) / 2));
  }, [viewMonth, numberOfMonths]);
  const handleMonthChange = useCallback(
    (month: Date) => {
      onViewMonthChange?.(addMonths(month, Math.floor((numberOfMonths - 1) / 2)));
    },
    [numberOfMonths, onViewMonthChange],
  );

  return (
    <DayPickerBase
      disabled={isDisabled}
      modifiers={modifiers}
      modifiersClassNames={{
        hover_outside_range: 'range-hover',
        hover_inside_range: 'range-hover-inside',
      }}
      onDayClick={handleDayClick}
      onDayMouseEnter={handleDayMouseEnter}
      onDayMouseLeave={handleDayMouseLeave}
      mode='default'
      displayMode={displayMode}
      cellSize={cellSize}
      locale={datePickerLocale}
      className={classNames('pds-date-picker', showAllHoverTarget && 'show-all-hover-calendar', className)}
      fixedWeeks
      showOutsideDays
      numberOfMonths={numberOfMonths}
      month={viewShiftedMonth}
      onMonthChange={handleMonthChange}
      components={{
        Caption: (props) => (
          <Caption
            {...props}
            onClickCaptionLabel={onClickCaptionLabel}
            displayMode={displayMode}
            currentMonth={viewShiftedMonth}
            locale={datePickerLocale}
            multipleMonths={numberOfMonths > 1}
          />
        ),
      }}
    />
  );
};
